import { Component, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { BadgeModule } from 'primeng/badge';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { WishlistService, ArkifiHubProductWishList } from '../../services/wishlist.service';
import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';

@Component({
  selector: 'app-wishlist',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ProgressSpinnerModule,
    CardModule,
    BadgeModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './wishlist.component.html',
  styleUrl: './wishlist.component.css'
})
export class WishlistComponent implements OnInit {
  platformId = signal<string>('');
  platformName = signal<string>('Store'); // Store name for navigation back
  wishListItems = signal<ArkifiHubProductWishList[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  
  // Pagination
  pageNumber = signal(1);
  pageSize = 20;
  totalItems = signal(0);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private wishlistService: WishlistService,
    private authService: AuthService,
    private settingsService: SettingsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin'], { queryParams: { returnUrl: '/wishlist' } });
      return;
    }

    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.platformId.set(id);
        // Try to get platform name from query params or state
        this.route.queryParams.subscribe(queryParams => {
          const name = queryParams['name'];
          if (name) {
            this.platformName.set(decodeURIComponent(name));
          }
        });
        // Check if name is in navigation state
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.state?.['platformName']) {
          this.platformName.set(navigation.extras.state['platformName']);
        }
        this.loadWishList();
      } else {
        // Try to get from query params or use default
        this.route.queryParams.subscribe(queryParams => {
          const platformId = queryParams['platformId'];
          if (platformId) {
            this.platformId.set(platformId);
            const name = queryParams['name'];
            if (name) {
              this.platformName.set(decodeURIComponent(name));
            }
            this.loadWishList();
          } else {
            this.error.set('Platform ID is required');
            this.loading.set(false);
          }
        });
      }
    });
  }

  loadWishList(): void {
    const platformId = this.platformId();
    if (!platformId) {
      this.error.set('Platform ID is required');
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.wishlistService.getWishList(platformId, this.pageNumber(), this.pageSize).subscribe({
      next: (response) => {
        if (response.success) {
          this.wishListItems.set(response.data);
          this.totalItems.set(response.data.length); // Note: API should return total count
        } else {
          this.error.set(response.message || 'Failed to load wishlist');
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load wishlist. Please try again later.');
        this.loading.set(false);
        console.error('Error loading wishlist:', err);
      }
    });
  }

  removeFromWishList(item: ArkifiHubProductWishList): void {
    this.wishlistService.removeFromWishList(item.id).subscribe({
      next: (response) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Product removed from wishlist'
          });
          // Remove from local list
          this.wishListItems.set(this.wishListItems().filter(i => i.id !== item.id));
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to remove product'
          });
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to remove product from wishlist'
        });
        console.error('Error removing from wishlist:', err);
      }
    });
  }

  clearWishList(): void {
    if (!confirm('Are you sure you want to clear your entire wishlist?')) {
      return;
    }

    const platformId = this.platformId();
    if (!platformId) return;

    this.wishlistService.clearWishList(platformId).subscribe({
      next: (response) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Wishlist cleared'
          });
          this.wishListItems.set([]);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to clear wishlist'
          });
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to clear wishlist'
        });
        console.error('Error clearing wishlist:', err);
      }
    });
  }

  getProductImage(item: ArkifiHubProductWishList): string {
    if (item.productImage) {
      return item.productImage;
    }
    return '/assets/placeholder-product.png';
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(price);
  }

  goToProduct(item: ArkifiHubProductWishList): void {
    // Navigate to product detail page
    this.router.navigate(['/store', item.platformId, 'product', item.productId]);
  }

  goBack(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    if (platformId) {
      // Navigate back to the store page with platformId and name
      this.router.navigate(['/store', platformId, encodeURIComponent(platformName)]);
    } else {
      // Fallback to marketplace if no platformId
      this.router.navigate(['/']);
    }
  }
}
